import React, { useState } from 'react'
import { toast } from 'sonner'
import { useQueryClient } from 'react-query'
import { format } from 'date-fns'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { timeEntryService } from '@/services/timeEntryService'

const hasOverlap = (newStart, newEnd, entries, excludeId = null) => {
  return entries
    .filter(e => e.id !== excludeId && e.end_time)
    .some(e => {
      const s = new Date(e.start_time)
      const en = new Date(e.end_time)
      return newStart < en && s < newEnd
    })
}

const ManualTimeEntryForm = ({ taskId, projectId, onClose, existingEntries = [] }) => {
  const queryClient = useQueryClient()
  const now = new Date()
  const [form, setForm] = useState({
    start_time: format(new Date(now.getTime() - 60 * 60 * 1000), "yyyy-MM-dd'T'HH:mm"),
    end_time: format(now, "yyyy-MM-dd'T'HH:mm"),
    note: ''
  })
  const [loading, setLoading] = useState(false)

  const nowMax = format(now, "yyyy-MM-dd'T'HH:mm")

  const handleSubmit = async (e) => {
    e.preventDefault()
    const currentNow = new Date()
    if (!form.start_time || !form.end_time) return toast.error('Start and end time are required')
    if (new Date(form.start_time) > currentNow) return toast.error('Start time cannot be in the future')
    if (new Date(form.end_time) > currentNow) return toast.error('End time cannot be in the future')
    if (new Date(form.end_time) <= new Date(form.start_time)) return toast.error('End time must be after start time')
    if (hasOverlap(new Date(form.start_time), new Date(form.end_time), existingEntries))
      return toast.error('This time range overlaps with an existing entry for this task')

    setLoading(true)
    try {
      await timeEntryService.createManual({
        task_id: taskId,
        start_time: new Date(form.start_time).toISOString(),
        end_time: new Date(form.end_time).toISOString(),
        note: form.note.trim() || null
      })
      queryClient.invalidateQueries(['projects', String(projectId), 'tasks', String(taskId)])
      toast.success('Time entry added')
      onClose?.()
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to add time entry')
    } finally {
      setLoading(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-3 p-3 border rounded-lg bg-muted/30">
      <div className="grid grid-cols-2 gap-2">
        <div className="space-y-1">
          <Label className="text-xs">Start</Label>
          <Input
            type="datetime-local"
            value={form.start_time}
            max={nowMax}
            onChange={(e) => setForm(f => ({ ...f, start_time: e.target.value }))}
            className="h-8 text-xs"
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">End</Label>
          <Input
            type="datetime-local"
            value={form.end_time}
            max={nowMax}
            onChange={(e) => setForm(f => ({ ...f, end_time: e.target.value }))}
            className="h-8 text-xs"
          />
        </div>
      </div>
      <div className="space-y-1">
        <Label className="text-xs">Note (optional)</Label>
        <Textarea
          value={form.note}
          onChange={(e) => setForm(f => ({ ...f, note: e.target.value }))}
          placeholder="What did you work on?"
          rows={2}
          className="text-sm resize-none"
        />
      </div>
      <div className="flex gap-2">
        <Button type="submit" size="sm" className="h-7 text-xs" disabled={loading}>
          {loading ? 'Adding...' : 'Add Entry'}
        </Button>
        <Button type="button" size="sm" variant="ghost" className="h-7 text-xs" onClick={() => onClose?.()}>
          Cancel
        </Button>
      </div>
    </form>
  )
}

export default ManualTimeEntryForm
